import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngrx/store';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { selectIsSafariBrowser } from '../../../core/store/selectors/app.selector';
import { selectPatientZipCode } from '../../../profile/store/selectors/profile.selector';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { IconButtonComponent } from '../../../shared/components/icon-button/icon-button.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { appleMapsUrl, googleMapsUrl } from '../../../shared/functions/address.functions';
import { Address } from '../../../shared/models/address/address';
import { Pharmacy } from '../../models/pharmacy';
import { PharmacySearchParams } from '../../models/pharmacy-search-params';
import { getPharmaciesSearch, nextPage } from '../../store/actions/pharmacy.actions';
import { selectIsLoading, selectPharmacies } from '../../store/selectors/pharmacy.selector';

@Component({
  selector: 'app-pharmacy-search-dialog',
  imports: [
    IconButtonComponent,
    MatFormField,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    NgxMaskDirective,
    IconComponent,
    NgxMaskPipe,
    ProgressSpinnerComponent,
    InfiniteScrollDirective,
    MatDividerModule,
    EmptyStateComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './pharmacy-search-dialog.component.html',
  styleUrl: './pharmacy-search-dialog.component.scss',
})
export class PharmacySearchDialogComponent {
  private store = inject(Store);
  private dialogRef = inject(MatDialogRef<PharmacySearchDialogComponent, Pharmacy>);

  readonly pharmacies = this.store.selectSignal(selectPharmacies);
  readonly isLoading = this.store.selectSignal(selectIsLoading);
  readonly isSafari = this.store.selectSignal(selectIsSafariBrowser);
  readonly patientZip = this.store.selectSignal(selectPatientZipCode);

  hasSearched: boolean = false;

  form = new FormGroup({
    name: new FormControl<string | null>(null, [Validators.minLength(3)]),
    city: new FormControl<string | null>(null, [Validators.minLength(3)]),
    state: new FormControl<string | null>(null, [Validators.pattern(/^[A-Za-z]{2}$/)]),
    zip: new FormControl<string | null>(null, [Validators.pattern(/^[0-9]{5}$/)]),
  });

  constructor() {
    if (this.patientZip() !== undefined && this.patientZip() !== null) {
      this.form.controls['zip'].setValue(this.patientZip());
    }
  }

  showSavedZipHint(): boolean {
    const savedZip = this.patientZip();
    return savedZip !== undefined && savedZip !== null && this.form.controls['zip'].value === savedZip;
  }

  getAddress(pharmacy: Pharmacy) {
    return new Address(
      pharmacy.addressLine1,
      pharmacy.addressLine2,
      pharmacy.city,
      pharmacy.state,
      pharmacy.postalCode
    );
  }

  getMapsUrl(event: MouseEvent, pharmacy: Pharmacy, isSafari: boolean) {
    event.stopPropagation();
    const address = this.getAddress(pharmacy);
    const url = isSafari ? appleMapsUrl(address) : googleMapsUrl(address);
    window.open(url, '_blank');
  }

  onScroll() {
    const searchParams: PharmacySearchParams = {
      name: this.form.controls['name'].value,
      city: this.form.controls['city'].value,
      state: this.form.controls['state'].value,
      postalCode: this.form.controls['zip'].value,
    };
    if (searchParams !== null) {
      this.store.dispatch(nextPage({ searchParams: searchParams }));
    }
  }

  pharmacyClick(pharmacy: Pharmacy) {
    this.dialogRef.close(pharmacy);
  }

  cancel() {
    this.dialogRef.close(null);
  }

  search() {
    const hasValue = Object.values(this.form.value).some(value => value !== null && value !== '');
    const currentErrors = this.form.errors || {};
    if (!hasValue) {
      this.form.setErrors({ ...currentErrors, noValues: true });
      this.form.markAllAsTouched();
      return;
    } else {
      if (this.form.hasError('noValues')) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { noValues, ...otherErrors } = currentErrors;
        this.form.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
      }
    }
    if (this.form.valid) {
      const searchParams: PharmacySearchParams = {
        name: this.form.controls['name'].value,
        city: this.form.controls['city'].value,
        state: this.form.controls['state'].value,
        postalCode: this.form.controls['zip'].value,
      };
      this.hasSearched = true;
      this.store.dispatch(getPharmaciesSearch({ searchParams }));
    }
  }
}
